import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  FileSpreadsheet,
  ArrowLeft,
  ArrowRight,
  Check,
  Download,
  AlertCircle,
  CheckCircle2,
  Loader2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { Badge } from '@/app/components/ui/badge';
import { toast } from '@/app/components/ui/simple-toaster';
import { parseCSVPreview } from '@/hooks/use-import';
import {
  PEOPLE_IMPORT_FIELDS,
  autoDetectPersonColumnMapping,
  useCommitPersonImport,
  useDownloadPeopleTemplate,
  usePreviewPersonImport,
  type ImportCommitResponse,
  type ImportPreviewResponse,
  type PersonColumnMapping,
  type PersonImportField,
} from '@/hooks/use-person-import';

type WizardStep = 'upload' | 'mapping' | 'preview' | 'result';

const STEPS: { id: WizardStep; label: string; number: number }[] = [
  { id: 'upload', label: 'Upload File', number: 1 },
  { id: 'mapping', label: 'Map Columns', number: 2 },
  { id: 'preview', label: 'Preview & Confirm', number: 3 },
  { id: 'result', label: 'Result', number: 4 },
];

const CERT_LABELS: Record<string, string> = {
  sprat_1: 'SPRAT L1',
  sprat_2: 'SPRAT L2',
  sprat_3: 'SPRAT L3',
  irata_1: 'IRATA L1',
  irata_2: 'IRATA L2',
  irata_3: 'IRATA L3',
  first_aid: 'First Aid',
  cpr: 'CPR',
  confined_space: 'Confined Space',
  fall_protection: 'Fall Protection',
  rigging: 'Rigging',
  osha_10: 'OSHA 10',
  osha_30: 'OSHA 30',
  twic: 'TWIC',
};

function toTitleCase(value: string) {
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatCertLabel(certType: string | null) {
  if (!certType) return '';
  return CERT_LABELS[certType] || toTitleCase(certType);
}

function toCsvValue(value: string | number | null | undefined) {
  if (value === null || value === undefined) return '';
  const text = String(value);
  if (/[,"\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function buildSkipReport(rows: ImportCommitResponse['rows']) {
  const skipped = rows.filter((row) => row.status !== 'ready');
  if (skipped.length === 0) return '';

  const lines = [
    ['Row', 'Status', 'Reason', 'First Name', 'Last Name', 'Email', 'Cert Note'],
    ...skipped.map((row) => [
      row.row,
      row.status,
      row.reason,
      row.data.first_name,
      row.data.last_name,
      row.data.email,
      row.cert?.status === 'unresolved' ? row.cert.reason : '',
    ]),
  ];

  return lines.map((line) => line.map(toCsvValue).join(',')).join('\n');
}

function groupedSkipReasons(rows: ImportPreviewResponse['rows'] | ImportCommitResponse['rows']) {
  const map = new Map<string, number[]>();
  rows.forEach((row) => {
    if (row.status === 'ready' || !row.reason) return;
    const existing = map.get(row.reason) ?? [];
    existing.push(row.row);
    map.set(row.reason, existing);
  });
  return [...map.entries()]
    .map(([reason, rowNumbers]) => ({ reason, rowNumbers, count: rowNumbers.length }))
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
}

export function PeopleImportWizardPage({ basePath = '/people' }: { basePath?: string }) {
  const navigate = useNavigate();
  const previewMutation = usePreviewPersonImport();
  const commitMutation = useCommitPersonImport();
  const downloadTemplateMutation = useDownloadPeopleTemplate();

  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvPreviewRows, setCsvPreviewRows] = useState<string[][]>([]);
  const [columnMapping, setColumnMapping] = useState<PersonColumnMapping[]>([]);
  const [previewResult, setPreviewResult] = useState<ImportPreviewResponse | null>(null);
  const [commitResult, setCommitResult] = useState<ImportCommitResponse | null>(null);
  const [expandedReasons, setExpandedReasons] = useState<Set<string>>(new Set());

  const currentStepIndex = STEPS.findIndex((step) => step.id === currentStep);

  const requiredMapped = useMemo(() => {
    const mapped = new Set(
      columnMapping.filter((mapping) => mapping.personField).map((mapping) => mapping.personField)
    );
    return PEOPLE_IMPORT_FIELDS.filter((field) => field.required).every((field) =>
      mapped.has(field.value)
    );
  }, [columnMapping]);

  const previewGroups = useMemo(() => {
    return groupedSkipReasons(previewResult?.rows ?? []);
  }, [previewResult]);

  const commitGroups = useMemo(() => {
    return groupedSkipReasons(commitResult?.rows ?? []);
  }, [commitResult]);

  const resetRunState = useCallback(() => {
    setPreviewResult(null);
    setCommitResult(null);
    setExpandedReasons(new Set());
  }, []);

  const resetAll = useCallback(() => {
    setCurrentStep('upload');
    setFile(null);
    setCsvHeaders([]);
    setCsvPreviewRows([]);
    setColumnMapping([]);
    resetRunState();
  }, [resetRunState]);

  const handleFileSelect = useCallback(
    async (selectedFile: File) => {
      setFile(selectedFile);
      resetRunState();

      try {
        const text = await selectedFile.text();
        const { headers, rows } = parseCSVPreview(text, 5);

        if (headers.length === 0) {
          toast.error('Invalid CSV file', {
            description: 'The file appears to be empty or invalid.',
          });
          return;
        }

        setCsvHeaders(headers);
        setCsvPreviewRows(rows);
        setColumnMapping(autoDetectPersonColumnMapping(headers));
        setCurrentStep('mapping');
      } catch (_error) {
        toast.error('Failed to read file', {
          description: 'Please ensure the file is a valid CSV.',
        });
      }
    },
    [resetRunState]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const droppedFile = e.dataTransfer.files[0];
      if (!droppedFile) return;
      if (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv')) {
        handleFileSelect(droppedFile);
      } else {
        toast.error('Invalid file type', {
          description: 'Please upload a CSV file.',
        });
      }
    },
    [handleFileSelect]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        handleFileSelect(selectedFile);
      }
    },
    [handleFileSelect]
  );

  const updateColumnMapping = useCallback((csvColumn: string, personField: PersonImportField | null) => {
    setColumnMapping((prev) =>
      prev.map((mapping) =>
        mapping.csvColumn === csvColumn ? { ...mapping, personField } : mapping
      )
    );
    setPreviewResult(null);
    setCommitResult(null);
  }, []);

  const goBack = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex].id);
    }
  }, [currentStepIndex]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      await downloadTemplateMutation.mutateAsync();
      toast.success('Template downloaded');
    } catch (_error) {
      toast.error('Failed to download template');
    }
  }, [downloadTemplateMutation]);

  const handlePreview = useCallback(async () => {
    if (!file) return;
    try {
      const result = await previewMutation.mutateAsync({ file, columnMapping });
      setPreviewResult(result);
      setCurrentStep('preview');
    } catch (error) {
      toast.error('Preview failed', {
        description: error instanceof Error ? error.message : 'An error occurred during preview.',
      });
    }
  }, [columnMapping, file, previewMutation]);

  const handleCommit = useCallback(async () => {
    if (!file) return;
    try {
      const result = await commitMutation.mutateAsync({ file, columnMapping });
      setCommitResult(result);
      setCurrentStep('result');

      const skipped =
        result.summary.duplicate + result.summary.invalid + result.summary.needs_review;
      if (skipped > 0) {
        toast.warning('Import completed with skips', {
          description: `${result.summary.ready} people imported, ${skipped} skipped.`,
        });
      } else {
        toast.success('Import completed', {
          description: `${result.summary.ready} people imported successfully.`,
        });
      }
    } catch (error) {
      toast.error('Import failed', {
        description: error instanceof Error ? error.message : 'An error occurred during import.',
      });
    }
  }, [columnMapping, commitMutation, file]);

  const handleDownloadSkipReport = useCallback(() => {
    const report = buildSkipReport(commitResult?.rows ?? previewResult?.rows ?? []);
    if (!report) return;

    const blob = new Blob([report], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'people_import_skip_report.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [commitResult, previewResult]);

  const renderStepIndicator = () => (
    <div className="flex flex-wrap items-center justify-center gap-y-2 mb-8">
      {STEPS.map((step, index) => (
        <div key={step.id} className="flex items-center">
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
              index < currentStepIndex
                ? 'bg-green-600 text-white'
                : index === currentStepIndex
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-600'
            }`}
          >
            {index < currentStepIndex ? <Check className="h-4 w-4" /> : step.number}
          </div>
          <span
            className={`ml-2 text-sm ${
              index === currentStepIndex ? 'font-medium text-gray-900' : 'text-gray-500'
            }`}
          >
            {step.label}
          </span>
          {index < STEPS.length - 1 && (
            <div
              className={`w-12 h-0.5 mx-4 ${
                index < currentStepIndex ? 'bg-green-600' : 'bg-gray-200'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderUploadStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Upload CSV File</CardTitle>
        <CardDescription>
          Upload a CSV file containing your people data. The file should have headers in the first row.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-gray-400 transition-colors cursor-pointer"
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => document.getElementById('people-import-file-input')?.click()}
        >
          <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-1">Drop your CSV file here</p>
          <p className="text-sm text-gray-500 mb-4">or click to browse</p>
          <input
            id="people-import-file-input"
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleFileInputChange}
          />
          <Button variant="outline" type="button">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Select File
          </Button>
        </div>

        {file && (
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">{file.name}</p>
              <p className="text-sm text-gray-500">{csvHeaders.length} columns detected</p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="font-medium text-gray-900">Need a template?</p>
            <p className="text-sm text-gray-500">Download a sample CSV with the expected columns.</p>
          </div>
          <Button variant="outline" onClick={handleDownloadTemplate} disabled={downloadTemplateMutation.isPending}>
            <Download className="h-4 w-4 mr-2" />
            Download Template
          </Button>
        </div>

      </CardContent>
    </Card>
  );

  const renderMappingStep = () => (
    <Card>
      <CardHeader>
        <CardTitle>Map Columns</CardTitle>
        <CardDescription>
          Match your CSV columns to the fields used by the import. First Name and Last Name are required.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/3">CSV Column</TableHead>
                <TableHead className="w-1/3">Sample Data</TableHead>
                <TableHead className="w-1/3">People Field</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {csvHeaders.map((header, index) => {
                const mapping = columnMapping.find((item) => item.csvColumn === header);
                const sampleData = csvPreviewRows[0]?.[index] || '';
                const selectedField = mapping?.personField;
                const isRequiredField =
                  selectedField &&
                  PEOPLE_IMPORT_FIELDS.some(
                    (field) => field.value === selectedField && field.required
                  );

                return (
                  <TableRow key={header}>
                    <TableCell className="font-medium">
                      {header}
                      {isRequiredField && (
                        <Badge variant="destructive" className="ml-2 text-xs">
                          Required
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-500 truncate max-w-[200px]">
                      {sampleData || <span className="italic">Empty</span>}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={mapping?.personField || 'skip'}
                        onValueChange={(value) =>
                          updateColumnMapping(
                            header,
                            value === 'skip' ? null : (value as PersonImportField)
                          )
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Skip this column" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="skip">
                            <span className="text-gray-500">Skip this column</span>
                          </SelectItem>
                          {PEOPLE_IMPORT_FIELDS.map((field) => {
                            const isAlreadyMapped = columnMapping.some(
                              (item) => item.personField === field.value && item.csvColumn !== header
                            );
                            return (
                              <SelectItem
                                key={field.value}
                                value={field.value}
                                disabled={isAlreadyMapped}
                              >
                                {field.label}
                                {field.required && ' *'}
                                {isAlreadyMapped && ' (already mapped)'}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {!requiredMapped && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-medium text-red-900">Missing required fields</p>
              <p className="text-sm text-red-700">
                You must map both First Name and Last Name to continue.
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-between">
          <Button variant="outline" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button onClick={handlePreview} disabled={!file || !requiredMapped || previewMutation.isPending}>
            {previewMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Previewing...
              </>
            ) : (
              <>
                Preview Import
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderPreviewStep = () => {
    const summary = previewResult?.summary;
    const rows = previewResult?.rows ?? [];
    const readyToCommit = (summary?.ready ?? 0) > 0;

    return (
      <Card>
        <CardHeader>
          <CardTitle>Preview & Confirm</CardTitle>
          <CardDescription>
            Review the preview results before creating any records.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-700">{summary.ready}</p>
                <p className="text-sm text-green-600">Ready</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-amber-700">{summary.duplicate}</p>
                <p className="text-sm text-amber-600">Duplicate</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-700">{summary.invalid}</p>
                <p className="text-sm text-red-600">Invalid</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-purple-700">{summary.needs_review}</p>
                <p className="text-sm text-purple-600">Needs Review</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-700">{summary.cert_ready}</p>
                <p className="text-sm text-blue-600">Cert Ready</p>
              </div>
            </div>
          )}

          {previewGroups.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-medium text-gray-900">Skip reasons</h3>
              <div className="space-y-2">
                {previewGroups.map((group) => (
                  <div key={group.reason} className="border rounded-lg">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50"
                      onClick={() => {
                        setExpandedReasons((prev) => {
                          const next = new Set(prev);
                          if (next.has(group.reason)) {
                            next.delete(group.reason);
                          } else {
                            next.add(group.reason);
                          }
                          return next;
                        });
                      }}
                    >
                      <div className="flex items-center gap-2">
                        {expandedReasons.has(group.reason) ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        )}
                        <span className="text-sm text-red-700">{group.reason}</span>
                      </div>
                      <Badge variant="secondary">{group.count} rows</Badge>
                    </button>
                    {expandedReasons.has(group.reason) && (
                      <div className="px-3 pb-3 pt-1 border-t bg-gray-50">
                        <p className="text-xs text-gray-500">
                          Affected rows: {group.rowNumbers.slice(0, 20).join(', ')}
                          {group.rowNumbers.length > 20 &&
                            ` and ${group.rowNumbers.length - 20} more`}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Row</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Certification</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.row}>
                    <TableCell className="font-medium">{row.row}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          row.status === 'ready'
                            ? 'default'
                            : row.status === 'duplicate' || row.status === 'needs_review'
                            ? 'secondary'
                            : 'destructive'
                        }
                        className={
                          row.status === 'ready'
                            ? 'bg-green-600'
                            : row.status === 'duplicate'
                            ? 'bg-amber-500 text-white hover:bg-amber-500'
                            : row.status === 'needs_review'
                            ? 'bg-purple-500 text-white hover:bg-purple-500'
                            : undefined
                        }
                      >
                        {row.status === 'needs_review'
                          ? 'needs review'
                          : row.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{`${row.data.first_name} ${row.data.last_name}`.trim()}</TableCell>
                    <TableCell>{row.data.email || '—'}</TableCell>
                    <TableCell className="text-sm text-gray-600">{row.reason || '—'}</TableCell>
                    <TableCell>
                      {row.cert?.status === 'ready' ? (
                        <Badge variant="outline">{formatCertLabel(row.cert.cert_type)}</Badge>
                      ) : row.cert?.status === 'unresolved' ? (
                        <span className="text-xs text-gray-500">unresolved cert</span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {summary && readyToCommit && (
            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-900">Preview ready</p>
                <p className="text-sm text-green-700">
                  {summary.ready} row{summary.ready === 1 ? '' : 's'} are ready to import.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={goBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Mapping
            </Button>
            <Button onClick={handleCommit} disabled={!readyToCommit || commitMutation.isPending}>
              {commitMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  Confirm &amp; Import
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderResultStep = () => {
    const summary = commitResult?.summary;
    const skipped =
      (summary?.duplicate ?? 0) +
      (summary?.invalid ?? 0) +
      (summary?.needs_review ?? 0);
    const totalProcessed = summary?.total ?? 0;

    return (
      <Card>
        <CardHeader>
          <CardTitle>Import Result</CardTitle>
          <CardDescription>
            {summary ? 'Your CSV import has completed.' : 'No import has been run yet.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {summary && (
            <>
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  {skipped > 0 ? (
                    <AlertCircle className="h-16 w-16 mx-auto text-amber-500 mb-4" />
                  ) : (
                    <CheckCircle2 className="h-16 w-16 mx-auto text-green-500 mb-4" />
                  )}
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {skipped > 0 ? 'Import Completed with Skips' : 'Import Successful'}
                  </h3>
                  <p className="text-gray-600">
                    {summary.ready} created, {skipped} skipped, {summary.cert_ready} certifications created.
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{totalProcessed} total rows processed</p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">{summary.ready}</p>
                  <p className="text-sm text-green-600">Created</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-700">{skipped}</p>
                  <p className="text-sm text-amber-600">Skipped</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-700">{summary.cert_ready}</p>
                  <p className="text-sm text-blue-600">Certs Created</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-700">{summary.total}</p>
                  <p className="text-sm text-gray-600">Total</p>
                </div>
              </div>

              {commitGroups.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900">Skip report</h3>
                    <Button variant="outline" size="sm" onClick={handleDownloadSkipReport}>
                      <Download className="h-4 w-4 mr-2" />
                      Download Skip Report
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {commitGroups.map((group) => (
                      <div key={group.reason} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <span className="text-sm text-amber-800">{group.reason}</span>
                        <Badge variant="secondary">{group.count} rows</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => navigate(basePath)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to People
            </Button>
            <Button onClick={resetAll}>
              Import Another File
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(basePath)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Import People CSV</h1>
          <p className="text-gray-600 mt-1">
            Preview rows first, then confirm the import when you&apos;re ready.
          </p>
        </div>
      </div>

      {renderStepIndicator()}

      {currentStep === 'upload' && renderUploadStep()}
      {currentStep === 'mapping' && renderMappingStep()}
      {currentStep === 'preview' && renderPreviewStep()}
      {currentStep === 'result' && renderResultStep()}
    </div>
  );
}
